import { useState, useEffect, useCallback, useRef } from "react";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, UserPlus, Edit2, Trash2, Loader2 } from "lucide-react";

const ROLES = [
  { value: "modeller", label: "Modeller" },
  { value: "detailer", label: "Detailer" },
  { value: "erector", label: "Erector" },
  { value: "checker", label: "Checker" },
  { value: "lead", label: "Lead" },
  { value: "coordinator", label: "Coordinator" },
  { value: "other", label: "Other" },
];

const ROLE_COLORS = {
  modeller: "bg-blue-100 text-blue-700 border-blue-200",
  detailer: "bg-cyan-100 text-cyan-700 border-cyan-200",
  erector: "bg-amber-100 text-amber-700 border-amber-200",
  checker: "bg-teal-100 text-teal-700 border-teal-200",
  lead: "bg-purple-100 text-purple-700 border-purple-200",
  coordinator: "bg-pink-100 text-pink-700 border-pink-200",
  other: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function ProjectTeamCard({ projectId, allUsers, canManage, onTeamChange }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [customRoleName, setCustomRoleName] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  // Stable ref for onTeamChange so it never causes fetchTeam to re-fire.
  const onTeamChangeRef = useRef(onTeamChange);
  useEffect(() => { onTeamChangeRef.current = onTeamChange; }, [onTeamChange]);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/team`);
      const t = res.data?.team || [];
      setTeam(t);
      onTeamChangeRef.current?.(t);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const openAdd = () => {
    setEditingMember(null);
    setSelectedUserId("");
    setSelectedRoles([]);
    setCustomRoleName("");
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditingMember(m);
    setSelectedUserId(m.user_id);
    setSelectedRoles(m.roles || []);
    setCustomRoleName(m.custom_role_name || "");
    setDialogOpen(true);
  };

  const toggleRole = (role) => {
    setSelectedRoles(rs => rs.includes(role) ? rs.filter(r => r !== role) : [...rs, role]);
  };

  const handleSave = async () => {
    if (!editingMember && !selectedUserId) {
      toast.error("Pick a user");
      return;
    }
    if (selectedRoles.length === 0) {
      toast.error("Select at least one role");
      return;
    }
    if (selectedRoles.includes("other") && !customRoleName.trim()) {
      toast.error("Provide a custom role name for 'Other'");
      return;
    }
    setSaving(true);
    try {
      if (editingMember) {
        await api.put(`/projects/${projectId}/team/${editingMember.user_id}`, {
          roles: selectedRoles,
          custom_role_name: selectedRoles.includes("other") ? customRoleName.trim() : null,
        });
        toast.success("Roles updated");
      } else {
        await api.post(`/projects/${projectId}/team`, {
          user_id: selectedUserId,
          roles: selectedRoles,
          custom_role_name: selectedRoles.includes("other") ? customRoleName.trim() : null,
        });
        toast.success("Team member added");
      }
      setDialogOpen(false);
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (m) => {
    if (!confirm(`Remove ${m.user_name} from the project team? Their existing task assignments will be preserved.`)) return;
    try {
      await api.delete(`/projects/${projectId}/team/${m.user_id}`);
      toast.success("Removed");
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const availableUsers = (allUsers || []).filter(u => !team.find(m => m.user_id === u.id));
  const filteredAvailable = availableUsers.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return ((u.first_name || "") + " " + (u.last_name || "")).toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q);
  });

  return (
    <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container" data-testid="project-team-card">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <CardTitle className="text-md3-title-large flex items-center gap-2">
            <Users className="h-5 w-5 text-md3-primary" />
            Project Team
            <Badge variant="outline" className="ml-1 text-xs">{team.length}</Badge>
          </CardTitle>
          {canManage && (
            <Button onClick={openAdd} size="sm" className="bg-md3-primary text-md3-on-primary hover:bg-md3-primary/90" data-testid="add-team-member-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-md3-primary" /></div>
        ) : team.length === 0 ? (
          <div className="text-center py-4 text-sm text-md3-on-surface-variant">
            No team members yet. {canManage && "Click 'Add User' to start building the project team."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {team.map(m => (
              <div key={m.user_id} className="bg-white rounded p-2 border border-md3-outline-variant flex items-center justify-between gap-2" data-testid={`team-row-${m.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{m.user_name}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(m.roles || []).map(r => (
                      <span key={r} className={`text-[10px] px-1.5 py-0.5 rounded border ${ROLE_COLORS[r] || ROLE_COLORS.other}`}>
                        {r === "other" && m.custom_role_name ? m.custom_role_name : ROLES.find(x => x.value === r)?.label || r}
                      </span>
                    ))}
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(m)} data-testid={`team-edit-${m.user_id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleRemove(m)} data-testid={`team-remove-${m.user_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingMember ? "Edit Roles" : "Add Team Member"}</DialogTitle>
            <DialogDescription>
              {editingMember ? editingMember.user_name : "Pick a user and assign roles for this project."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editingMember && (
              <div>
                <label className="text-sm font-medium">User</label>
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-white mb-2"
                  data-testid="team-user-search"
                />
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="bg-white" data-testid="team-user-select"><SelectValue placeholder="Pick a user..." /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {filteredAvailable.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {(u.first_name || "")} {(u.last_name || "")} {u.department ? `· ${u.department}` : ""}
                      </SelectItem>
                    ))}
                    {filteredAvailable.length === 0 && <div className="p-2 text-xs text-md3-on-surface-variant">No users available</div>}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Roles *</label>
              <div className="grid grid-cols-2 gap-1 mt-1">
                {ROLES.map(r => (
                  <label key={r.value} className="flex items-center gap-2 text-sm border rounded px-2 py-1 cursor-pointer hover:bg-md3-surface-container-high">
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(r.value)}
                      onChange={() => toggleRole(r.value)}
                      data-testid={`team-role-${r.value}`}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
            {selectedRoles.includes("other") && (
              <div>
                <label className="text-sm font-medium">Custom Role Name *</label>
                <Input
                  value={customRoleName}
                  onChange={(e) => setCustomRoleName(e.target.value)}
                  placeholder="e.g. QA Reviewer"
                  className="bg-white"
                  data-testid="team-custom-role"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-md3-primary text-md3-on-primary hover:bg-md3-primary/90" data-testid="team-save-btn">
              {saving ? "Saving..." : (editingMember ? "Update" : "Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
